import { Reducer } from '@reduxjs/toolkit';

import type { StartListening } from './createAppStore';

/** localStorage by default, or anything with the same two methods (sessionStorage, a test double). */
export type PersistStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface PersistConfig<S> {
  /** The storage key, one per app. */
  key: string;
  /** The fields kept, per slice: `{ auth: ['loggedIn'] }`. */
  paths: { [K in keyof S]?: (keyof NonNullable<S[K]>)[] };
  /** Bump when a kept field changes shape, so a save in the old shape is dropped. */
  version?: number;
  storage?: PersistStorage;
}

type Picked = Record<string, Record<string, unknown>>;

interface Saved {
  version: number;
  state: Picked;
}

/** Undefined on the server, so a request never reads another's save. Reading it throws where storage is blocked. */
export const browserStorage = (): PersistStorage | undefined =>
  typeof window === 'undefined' ? undefined : window.localStorage;

const pick = (state: any, paths: Record<string, string[]>): Picked =>
  Object.fromEntries(
    Object.entries(paths).map(([slice, fields]) => [
      slice,
      Object.fromEntries(fields.map((field) => [field, state?.[slice]?.[field]])),
    ])
  );

/** The saved fields over each slice's state, since a preloaded slice replaces its initial state whole. */
export function readPersisted<S, P>(
  reducer: Reducer<S, any, P>,
  { key, paths, version = 0, storage }: PersistConfig<S>,
  preloadedState?: P
): P | undefined {
  let saved: Saved | null;
  try {
    saved = JSON.parse((storage ?? browserStorage())?.getItem(key) ?? 'null');
  } catch {
    return preloadedState; // blocked storage, or a value we did not write
  }
  if (saved?.version !== version || !saved.state) return preloadedState;

  const state: any = { ...reducer(preloadedState, { type: '@@persist/init' }) };
  // Only the configured fields, so one dropped from the config is not restored from an old save.
  for (const [slice, fields] of Object.entries(paths as Record<string, string[]>)) {
    const savedSlice = saved.state[slice];
    if (!savedSlice) continue;
    const restored = fields.filter((field) => field in savedSlice).map((field) => [field, savedSlice[field]]);
    state[slice] = { ...state[slice], ...Object.fromEntries(restored) };
  }

  return state;
}

export const persistListener =
  <S>({ key, paths, version = 0, storage }: PersistConfig<S>) =>
  (startListening: StartListening) => {
    const fields = paths as Record<string, string[]>;

    startListening({
      predicate: (_action, current: any, previous: any) =>
        Object.entries(fields).some(([slice, names]) =>
          names.some((name) => current[slice]?.[name] !== previous[slice]?.[name])
        ),
      effect: (_action, { getState }) => {
        const saved: Saved = { version, state: pick(getState(), fields) };
        try {
          (storage ?? browserStorage())?.setItem(key, JSON.stringify(saved));
        } catch {
          // full or blocked storage: the state still holds
        }
      },
    });
  };
